import type {
  InventoryItem,
  InventoryItemUpdate,
  ItemResearch,
  ItemResearchClaim,
  ItemResearchEvidence,
  ItemResearchHypothesis,
  ItemResearchRun,
  Money,
  PhotoInput,
  ResearchConfidence,
  ResearchEvidenceDecision,
  ResearchEvidenceType,
  ResearchSellerDecision
} from "./types";

export type ItemResearchCapability =
  | "visual_identification"
  | "visual_web_search"
  | "active_comparables"
  | "sold_comparables"
  | "catalogue_reference"
  | "open_web_research"
  | "claim_verification";

export interface ItemResearchProviderInput {
  item: InventoryItem;
  photos: PhotoInput[];
  query?: string;
}

export interface ItemResearchProviderProposal {
  research: ItemResearch;
  capabilitiesUsed: ItemResearchCapability[];
}

export interface ItemResearchProvider {
  id: string;
  label: string;
  capabilities: ItemResearchCapability[];
  research: (input: ItemResearchProviderInput) => Promise<ItemResearchProviderProposal>;
}

export const manualResearchProvider = {
  id: "seller_manual",
  label: "Seller research",
  mode: "manual"
} as const;

const statuses = new Set(["in_review", "ready_to_refine", "refined"]);
const confidenceLevels = new Set<ResearchConfidence>(["low", "medium", "high"]);
const evidenceTypes = new Set<ResearchEvidenceType>(["active_listing", "sold_comparable", "reference"]);
const evidenceDecisions = new Set<ResearchEvidenceDecision>(["included", "excluded"]);
const providerModes = new Set(["manual", "connected"]);
const researchDepths = new Set(["quick", "standard", "deep"]);
const researchFocuses = new Set(["complete", "identify", "verify", "price_position", "custom"]);
const runStatuses = new Set(["queued", "researching", "verifying", "needs_input", "ready", "failed"]);
const planStatuses = new Set(["pending", "active", "complete", "blocked"]);
const observationBases = new Set(["photo", "seller", "inference"]);
const sourceCategories = new Set(["primary", "institutional", "specialist", "community", "marketplace", "unverified"]);
const evidenceRelationships = new Set(["supports", "contradicts", "context"]);
const claimKinds = new Set(["identity", "maker", "date", "origin", "material", "authenticity", "condition", "history", "valuation"]);
const claimVerdicts = new Set(["supported", "contested", "unverified"]);
const sellerDecisions = new Set(["pending", "accepted", "rejected", "needs_evidence"]);
const requestedInputs = new Set(["photo", "measurement", "text"]);
const questionPriorities = new Set(["high", "medium", "low"]);
const searchMethods = new Set(["visual", "web", "marketplace", "internal"]);
const MAX_EVIDENCE_RECORDS = 50;
const MAX_RESEARCH_CLAIMS = 40;
const MAX_RESEARCH_QUESTIONS = 12;

export function validateItemResearch(research: ItemResearch, itemCurrency: string): ItemResearch {
  if (!research || typeof research !== "object") {
    throw new Error("Item research is invalid.");
  }
  if (!statuses.has(research.status)) {
    throw new Error("Choose a valid research status.");
  }
  if (!research.identification || !confidenceLevels.has(research.identification.confidence)) {
    throw new Error("Choose an identification confidence.");
  }
  if (!Array.isArray(research.evidence) || research.evidence.length > MAX_EVIDENCE_RECORDS) {
    throw new Error(`Research can contain up to ${MAX_EVIDENCE_RECORDS} evidence records.`);
  }
  if (!research.provider?.id?.trim() || !research.provider.label?.trim() || !providerModes.has(research.provider.mode)) {
    throw new Error("Research must identify its evidence provider.");
  }
  if (!validTimestamp(research.updatedAt)) {
    throw new Error("Research must have a valid update time.");
  }

  const ids = new Set<string>();
  const evidence = research.evidence.map((record) => validateEvidence(record, ids));
  const targetPrice = validateMoney(research.refinement?.targetPrice, itemCurrency, "Research target price");
  const minimumPrice = validateMoney(research.refinement?.minimumPrice, itemCurrency, "Research minimum price");
  if (targetPrice && minimumPrice && minimumPrice.amountMinor > targetPrice.amountMinor) {
    throw new Error("Research minimum price cannot exceed the target price.");
  }

  return {
    status: research.status,
    review: research.review ? {
      reviewedAt: optionalTimestamp(research.review.reviewedAt, "Research review time") ?? research.updatedAt,
      baseline: optionalText(research.review.baseline),
      photoBaseline: optionalText(research.review.photoBaseline),
      selectedFields: Array.isArray(research.review.selectedFields) ? research.review.selectedFields.filter((field) => ["title", "description", "brand", "material", "targetPriceMinor", "condition"].includes(field)) : [],
      conditionPending: research.review.conditionPending === true
    } : undefined,
    identification: {
      maker: optionalText(research.identification.maker),
      model: optionalText(research.identification.model),
      period: optionalText(research.identification.period),
      origin: optionalText(research.identification.origin),
      material: optionalText(research.identification.material),
      identifiers: optionalText(research.identification.identifiers),
      sellerConfidence: research.identification.sellerConfidence && confidenceLevels.has(research.identification.sellerConfidence)
        ? research.identification.sellerConfidence
        : undefined,
      confidence: research.identification.confidence,
      rationale: optionalText(research.identification.rationale),
      confirmedAt: optionalTimestamp(research.identification.confirmedAt, "Identification confirmation time")
    },
    evidence,
    refinement: {
      positioning: optionalText(research.refinement?.positioning),
      title: optionalText(research.refinement?.title),
      description: optionalText(research.refinement?.description),
      targetPrice,
      minimumPrice,
      pricingRationale: optionalText(research.refinement?.pricingRationale)
    },
    run: research.run ? validateResearchRun(research.run, evidence.map((record) => record.id)) : undefined,
    provider: {
      id: research.provider.id.trim(),
      label: research.provider.label.trim(),
      mode: research.provider.mode,
      generatedAt: optionalTimestamp(research.provider.generatedAt, "Provider generation time")
    },
    updatedAt: research.updatedAt
  };
}

export function itemUpdateFromResearch(research: ItemResearch): InventoryItemUpdate {
  const refinement = research.refinement;
  const identification = research.identification;
  const identityClaim = primaryIdentityResearchClaim(research);
  const canApplyAttributedIdentity = !identityClaim || identityClaim.sellerDecision === "accepted";
  const canApplyPricing = researchPricingIsApplicable(research);
  const provisionalAttribution =
    identityClaim?.sellerDecision === "accepted"
    && identification.sellerConfidence !== "high";
  const update: InventoryItemUpdate = {
    research: {
      ...research,
      status: "refined"
    }
  };
  if (canApplyAttributedIdentity && refinement.title) {
    update.title = provisionalAttribution
      ? provisionalAttributionTitle(refinement.title)
      : refinement.title;
  }
  if (canApplyAttributedIdentity && refinement.description) {
    update.description = provisionalAttribution
      ? provisionalAttributionDescription(refinement.description)
      : refinement.description;
  }
  if (canApplyAttributedIdentity && !provisionalAttribution && identification.maker) {
    update.brand = identification.maker;
  }
  if (canApplyAttributedIdentity && identification.material) update.material = identification.material;
  if (canApplyPricing && refinement.targetPrice) update.targetPriceMinor = refinement.targetPrice.amountMinor;
  if (canApplyPricing) update.minimumPriceMinor = refinement.minimumPrice?.amountMinor ?? null;
  return update;
}

function provisionalAttributionTitle(title: string) {
  const normalized = title.trim();
  return /^(?:attributed to|possibly|probably)\b/i.test(normalized)
    ? normalized
    : `Attributed to ${normalized}`;
}

function provisionalAttributionDescription(description: string) {
  const normalized = description.trim();
  return /^attribution (?:is|remains) provisional\b/i.test(normalized)
    ? normalized
    : `Attribution is provisional and has not been independently verified. ${normalized}`;
}

export function researchIdentityLabel(research: ItemResearch | undefined) {
  if (!research) return undefined;
  return [research.identification.maker, research.identification.model, research.identification.period].filter(Boolean).join(" · ") || undefined;
}

export function includedResearchEvidence(research: ItemResearch | undefined) {
  return research?.evidence.filter((record) => record.decision === "included") ?? [];
}

export function isSourceBackedVisualEvidence(record: ItemResearchEvidence) {
  return record.imageProvenance === "source"
    && Boolean(record.imageURL)
    && Boolean(record.sourceURL);
}

export function acceptedResearchClaims(research: ItemResearch | undefined) {
  return reviewableResearchClaims(research).filter((claim) => claim.sellerDecision === "accepted");
}

export function reviewableResearchClaims(research: ItemResearch | undefined) {
  const reviewCandidate = sellerReviewCandidateIdentification(research);
  return research?.run?.claims.filter((claim) => {
    if (isLegacyPreviewPlaceholderClaim(claim)) return false;
    if (claim.kind === "identity" && claim.verdict === "unverified" && !reviewCandidate) return false;
    return true;
  }) ?? [];
}

export function pendingResearchClaims(research: ItemResearch | undefined) {
  return reviewableResearchClaims(research).filter((claim) => claim.sellerDecision === "pending");
}

export type ResearchFindingsMode = "needs_evidence" | "candidate_review" | "accepted" | "rejected";

export function primaryIdentityResearchClaim(research: ItemResearch | undefined) {
  return reviewableResearchClaims(research).find((claim) => claim.kind === "identity");
}

export function alternativeResearchHypotheses(research: ItemResearch | undefined): ItemResearchHypothesis[] {
  if (!research?.run) return [];
  const identityClaim = primaryIdentityResearchClaim(research);
  const candidateIdentity = normalizeHypothesisIdentity([
    research.identification.maker,
    research.identification.model
  ].filter(Boolean).join(" "));

  return research.run.hypotheses.filter((hypothesis) => {
    if (
      hypothesis.label === "Identification still open"
      || /no live sources were queried|local preview/i.test(hypothesis.summary)
    ) {
      return false;
    }
    if (identityClaim && hypothesis.supportingClaimIDs.includes(identityClaim.id)) {
      return false;
    }
    const hypothesisIdentity = normalizeHypothesisIdentity(hypothesis.label);
    return !candidateIdentity
      || !hypothesisIdentity
      || (
        !hypothesisIdentity.includes(candidateIdentity)
        && !candidateIdentity.includes(hypothesisIdentity)
      );
  });
}

export function researchPricingIsApplicable(research: ItemResearch | undefined) {
  if (!research) return false;
  const identityClaim = primaryIdentityResearchClaim(research);
  if (!identityClaim || identityClaim.sellerDecision === "accepted") return true;
  return research.run?.claims.some(
    (claim) => claim.kind === "valuation" && claim.sellerDecision === "accepted"
  ) ?? false;
}

function normalizeHypothesisIdentity(value: string) {
  return value
    .normalize("NFKD")
    .toLocaleLowerCase("en-GB")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export const RESEARCH_RUN_STALE_AFTER_MS = 5 * 60 * 1_000;

export function researchRunIsStale(
  run: ItemResearch["run"] | undefined,
  now = Date.now(),
  thresholdMs = RESEARCH_RUN_STALE_AFTER_MS
) {
  if (!run || !["queued", "researching", "verifying"].includes(run.status)) return false;
  const updatedAt = Date.parse(run.updatedAt);
  return Number.isFinite(updatedAt) && now - updatedAt >= thresholdMs;
}

export function researchFindingsMode(research: ItemResearch | undefined): ResearchFindingsMode {
  const identityClaim = primaryIdentityResearchClaim(research);
  if (!identityClaim || identityClaim.sellerDecision === "needs_evidence") return "needs_evidence";
  if (identityClaim.sellerDecision === "pending") return "candidate_review";
  return identityClaim.sellerDecision;
}

function isLegacyPreviewPlaceholderClaim(claim: ItemResearchClaim) {
  if (
    claim.id !== "claim_local_identity"
    || claim.verdict !== "unverified"
    || claim.evidenceIDs.length > 0
  ) {
    return false;
  }
  return (
    /^The item is currently described as\b/i.test(claim.statement)
    || /live visual and web verification|local preview|no live sources were queried/i.test(claim.rationale)
  );
}

export function sellerReviewCandidateIdentification(research: ItemResearch | undefined) {
  const payload = recordValue(research?.run?.automation?.candidatePayload);
  const candidate = recordValue(payload.sellerReviewCandidate);
  const identification = recordValue(candidate.identification);
  const maker = optionalTextValue(identification.maker);
  const model = optionalTextValue(identification.model);
  if (!maker || !model) return undefined;
  return {
    maker,
    model,
    rationale: optionalTextValue(identification.rationale)
      ?? optionalTextValue(candidate.rationale)
  };
}

export function rejectedResearchIdentityLabel(research: ItemResearch | undefined) {
  const identityClaim = primaryIdentityResearchClaim(research);
  if (identityClaim?.sellerDecision !== "rejected") return undefined;
  const candidate = sellerReviewCandidateIdentification(research);
  return researchIdentityLabel(research)
    ?? (candidate ? [candidate.maker, candidate.model].join(" · ") : undefined)
    ?? identityClaim.statement;
}

export function researchSellerContextForNextRun(
  research: ItemResearch | undefined,
  sellerContext: string,
  answeredQuestions?: string
) {
  const rejectedIdentity = rejectedResearchIdentityLabel(research);
  return [
    sellerContext.trim(),
    answeredQuestions?.trim() ? `Seller answers from the previous run:\n${answeredQuestions.trim()}` : "",
    rejectedIdentity
      ? `Previously rejected identification: ${rejectedIdentity}. Do not propose it again unless the seller first undoes that decision.`
      : ""
  ].filter(Boolean).join("\n\n") || undefined;
}

export function researchNeedsRerun(research: ItemResearch, selectedPhotoIDs: string[]) {
  const run = research.run;
  if (!run) return false;
  const previousPhotos = new Set(run.photoIDs);
  const selectedPhotos = new Set(selectedPhotoIDs);
  const photosChanged = previousPhotos.size !== selectedPhotos.size
    || [...previousPhotos].some((photoID) => !selectedPhotos.has(photoID));
  if (photosChanged) return true;
  const completedAt = run.completedAt ? Date.parse(run.completedAt) : Number.NaN;
  return run.questions.some((question) => {
    if (!question.answer?.trim() || !question.answeredAt) return false;
    return !Number.isFinite(completedAt) || Date.parse(question.answeredAt) > completedAt;
  });
}

export function withResearchClaimDecision(
  research: ItemResearch,
  claimID: string,
  decision: ResearchSellerDecision
) {
  if (!research.run) return research;
  const claim = research.run.claims.find((candidate) => candidate.id === claimID);
  const reviewCandidate = claim?.kind === "identity" && claim.verdict === "unverified"
    ? sellerReviewCandidateIdentification(research)
    : undefined;
  const candidateIsCurrent = reviewCandidate
    && research.identification.maker === reviewCandidate.maker
    && research.identification.model === reviewCandidate.model;
  const restoreRejectedCandidate = reviewCandidate
    && (claim?.sellerDecision === "rejected" || claim?.sellerDecision === "needs_evidence")
    && decision !== "rejected"
    && decision !== "needs_evidence"
    && !research.identification.maker
    && !research.identification.model;
  let identification = candidateIsCurrent && (decision === "rejected" || decision === "needs_evidence")
    ? {
        ...research.identification,
        maker: undefined,
        model: undefined,
        sellerConfidence: undefined,
        confidence: "low" as const,
        rationale: decision === "rejected"
          ? "The proposed identity was rejected by the seller."
          : "The seller kept the proposed identity unresolved pending stronger evidence."
      }
    : restoreRejectedCandidate
      ? {
          ...research.identification,
          maker: reviewCandidate.maker,
          model: reviewCandidate.model,
          sellerConfidence: undefined,
          confidence: "low" as const,
          rationale: reviewCandidate.rationale
        }
      : research.identification;
  if (claim?.kind === "identity" && decision === "accepted") {
    identification = {
      ...identification,
      confidence: claim.confidence,
      sellerConfidence: undefined
    };
  }
  return {
    ...research,
    identification,
    run: {
      ...research.run,
      claims: research.run.claims.map((candidate) => candidate.id === claimID
        ? { ...candidate, sellerDecision: decision }
        : candidate)
    }
  };
}

function validateEvidence(record: ItemResearchEvidence, ids: Set<string>): ItemResearchEvidence {
  if (!record?.id?.trim() || ids.has(record.id)) {
    throw new Error("Every research evidence record must have a unique ID.");
  }
  ids.add(record.id);
  if (!evidenceTypes.has(record.type) || !evidenceDecisions.has(record.decision)) {
    throw new Error("Choose a valid evidence type and decision.");
  }
  if (!record.sourceName?.trim() || !record.title?.trim()) {
    throw new Error("Every evidence record needs a source and title.");
  }
  const sourceURL = optionalText(record.sourceURL);
  if (sourceURL && !isWebURL(sourceURL)) {
    throw new Error("Evidence links must use http or https.");
  }
  if (!validTimestamp(record.observedAt)) {
    throw new Error("Every evidence record needs a valid observation date.");
  }
  const imageURL = validateOptionalEvidenceImageURL(record.imageURL);
  const imageProvenance = optionalEnum(
    record.imageProvenance,
    new Set(["source", "synthetic_demo"]),
    "evidence image provenance"
  );
  const imageDisclosure = optionalText(record.imageDisclosure);
  if (imageProvenance === "synthetic_demo" && (!imageURL || !imageDisclosure)) {
    throw new Error("Synthetic research images require an image and an explicit disclosure.");
  }
  return {
    id: record.id,
    type: record.type,
    decision: record.decision,
    sourceName: record.sourceName.trim(),
    title: record.title.trim(),
    sourceURL,
    price: validateSourceMoney(record.price, "Evidence price"),
    condition: optionalText(record.condition),
    observedAt: record.observedAt,
    notes: optionalText(record.notes),
    sourceCategory: optionalEnum(record.sourceCategory, sourceCategories, "evidence source category"),
    relationship: optionalEnum(record.relationship, evidenceRelationships, "evidence relationship"),
    confidence: optionalEnum(record.confidence, confidenceLevels, "evidence confidence"),
    excerpt: optionalText(record.excerpt),
    imageURL,
    imageProvenance,
    imageDisclosure,
    retrievedAt: optionalTimestamp(record.retrievedAt, "Evidence retrieval time"),
    independenceKey: optionalText(record.independenceKey),
    priceVerification: record.priceVerification ? {
      verdict: requiredEnum(record.priceVerification.verdict, claimVerdicts, "price verification verdict"),
      saleConfirmed: record.priceVerification.saleConfirmed === true,
      rationale: requiredText(record.priceVerification.rationale, "Price verification rationale"),
      sourceURL: validateSourceURL(record.priceVerification.sourceURL),
      pageType: optionalEnum(record.priceVerification.pageType, new Set(["item", "collection", "search", "unavailable"]), "price source page type"),
      itemMatch: optionalEnum(record.priceVerification.itemMatch, new Set(["same", "variant", "different", "unknown"]), "price source item match"),
      listingStatus: optionalEnum(record.priceVerification.listingStatus, new Set(["active", "sold", "ended", "out_of_stock", "unknown"]), "price source listing status"),
      observedPrice: validateSourceMoney(record.priceVerification.observedPrice, "Observed source price"),
      checkedAt: optionalTimestamp(record.priceVerification.checkedAt, "Price source check time")
    } : undefined
  };
}

function validateResearchRun(run: ItemResearchRun, evidenceIDs: string[]): ItemResearchRun {
  if (!run?.id?.trim() || !researchDepths.has(run.depth) || !runStatuses.has(run.status)) {
    throw new Error("Research run details are invalid.");
  }
  if (!Number.isInteger(run.progress) || run.progress < 0 || run.progress > 100) {
    throw new Error("Research progress must be between 0 and 100.");
  }
  if (!validTimestamp(run.startedAt) || !validTimestamp(run.updatedAt)) {
    throw new Error("Research run timestamps are invalid.");
  }
  if (!Array.isArray(run.claims) || run.claims.length > MAX_RESEARCH_CLAIMS) {
    throw new Error(`Research can contain up to ${MAX_RESEARCH_CLAIMS} claims.`);
  }
  if (!Array.isArray(run.questions) || run.questions.length > MAX_RESEARCH_QUESTIONS) {
    throw new Error(`Research can contain up to ${MAX_RESEARCH_QUESTIONS} follow-up questions.`);
  }
  const validEvidenceIDs = new Set(evidenceIDs);
  const claimIDs = new Set<string>();
  const claims = run.claims.map((claim) => validateClaim(claim, claimIDs, validEvidenceIDs));
  const validClaimIDs = new Set(claims.map((claim) => claim.id));
  const focus = optionalEnum(run.focus, researchFocuses, "research focus");
  const customPriority = optionalText(run.customPriority);
  const photoIDs = run.photoIDs === undefined ? undefined : validateResearchPhotoIDs(run.photoIDs);
  if (focus === "custom" && !customPriority) {
    throw new Error("Custom research needs a question or priority.");
  }
  return {
    id: run.id.trim(),
    outputVersion: Number.isInteger(run.outputVersion) && (run.outputVersion ?? 0) > 0 ? run.outputVersion : undefined,
    requestID: optionalText(run.requestID),
    requestSignature: optionalText(run.requestSignature),
    photoBaseline: optionalText(run.photoBaseline),
    baseline: optionalText(run.baseline),
    depth: run.depth,
    focus,
    templateVersion: optionalText(run.templateVersion),
    customPriority,
    photoIDs,
    status: run.status,
    phase: requiredText(run.phase, "Research phase"),
    progress: run.progress,
    brief: requiredText(run.brief, "Research brief"),
    sellerContext: optionalText(run.sellerContext),
    plan: Array.isArray(run.plan)
      ? run.plan.slice(0, 12).map((step) => ({
          id: requiredText(step.id, "Research plan step ID"),
          label: requiredText(step.label, "Research plan step"),
          detail: requiredText(step.detail, "Research plan detail"),
          status: requiredEnum(step.status, planStatuses, "research plan status")
        }))
      : [],
    observations: Array.isArray(run.observations)
      ? run.observations.slice(0, 40).map((observation) => ({
          id: requiredText(observation.id, "Observation ID"),
          label: requiredText(observation.label, "Observation label"),
          value: requiredText(observation.value, "Observation value"),
          basis: requiredEnum(observation.basis, observationBases, "observation basis"),
          confidence: requiredEnum(observation.confidence, confidenceLevels, "observation confidence"),
          photoID: optionalText(observation.photoID)
        }))
      : [],
    hypotheses: Array.isArray(run.hypotheses)
      ? run.hypotheses.slice(0, 8).map((hypothesis) => ({
          id: requiredText(hypothesis.id, "Hypothesis ID"),
          label: requiredText(hypothesis.label, "Hypothesis label"),
          confidence: requiredEnum(hypothesis.confidence, confidenceLevels, "hypothesis confidence"),
          summary: requiredText(hypothesis.summary, "Hypothesis summary"),
          supportingClaimIDs: validReferences(hypothesis.supportingClaimIDs, validClaimIDs),
          conflictingClaimIDs: validReferences(hypothesis.conflictingClaimIDs, validClaimIDs)
        }))
      : [],
    claims,
    questions: run.questions.map((question) => ({
      id: requiredText(question.id, "Research question ID"),
      prompt: requiredText(question.prompt, "Research question"),
      reason: requiredText(question.reason, "Research question reason"),
      requestedInput: requiredEnum(question.requestedInput, requestedInputs, "requested input"),
      priority: requiredEnum(question.priority, questionPriorities, "research question priority"),
      answer: optionalText(question.answer),
      answeredAt: optionalTimestamp(question.answeredAt, "Research question answer time")
    })),
    searches: Array.isArray(run.searches)
      ? run.searches.slice(0, 40).map((search) => ({
          id: requiredText(search.id, "Research search ID"),
          query: requiredText(search.query, "Research search query"),
          method: requiredEnum(search.method, searchMethods, "research search method"),
          resultCount: Number.isInteger(search.resultCount) && (search.resultCount ?? -1) >= 0 ? search.resultCount : undefined
        }))
      : [],
    verification: run.verification
      ? {
          checkedClaimCount: nonNegativeInteger(run.verification.checkedClaimCount, "Checked claim count"),
          supportedClaimCount: nonNegativeInteger(run.verification.supportedClaimCount, "Supported claim count"),
          contestedClaimCount: nonNegativeInteger(run.verification.contestedClaimCount, "Contested claim count"),
          unsupportedClaimCount: nonNegativeInteger(run.verification.unsupportedClaimCount, "Unsupported claim count"),
          summary: requiredText(run.verification.summary, "Verification summary"),
          completedAt: optionalTimestamp(run.verification.completedAt, "Verification completion time")!
        }
      : undefined,
    automation: run.automation
      ? {
          providerRunID: optionalText(run.automation.providerRunID),
          verifierRunID: optionalText(run.automation.verifierRunID),
          workflowInstanceID: optionalText(run.automation.workflowInstanceID),
          candidatePayload: run.automation.candidatePayload
        }
      : undefined,
    startedAt: run.startedAt,
    updatedAt: run.updatedAt,
    completedAt: optionalTimestamp(run.completedAt, "Research completion time"),
    error: optionalText(run.error)
  };
}

function validateResearchPhotoIDs(values: string[]) {
  if (!Array.isArray(values) || values.length === 0 || values.length > 8) {
    throw new Error("Research must use between one and eight item photos.");
  }
  const photoIDs = values.map((value) => requiredText(value, "Research photo ID"));
  if (new Set(photoIDs).size !== photoIDs.length) {
    throw new Error("Research photo IDs must be unique.");
  }
  return photoIDs;
}

function validateClaim(claim: ItemResearchClaim, ids: Set<string>, evidenceIDs: Set<string>): ItemResearchClaim {
  const id = requiredText(claim.id, "Research claim ID");
  if (ids.has(id)) throw new Error("Every research claim must have a unique ID.");
  ids.add(id);
  return {
    id,
    kind: requiredEnum(claim.kind, claimKinds, "research claim kind"),
    statement: requiredText(claim.statement, "Research claim"),
    verdict: requiredEnum(claim.verdict, claimVerdicts, "research claim verdict"),
    confidence: requiredEnum(claim.confidence, confidenceLevels, "research claim confidence"),
    rationale: requiredText(claim.rationale, "Research claim rationale"),
    evidenceIDs: validReferences(claim.evidenceIDs, evidenceIDs),
    sellerDecision: requiredEnum(claim.sellerDecision, sellerDecisions, "seller claim decision")
  };
}

function validateSourceURL(value: string | undefined) {
  const url = optionalText(value);
  if (url && !isWebURL(url)) throw new Error("Price source links must use http or https.");
  return url;
}

function validateSourceMoney(value: Money | undefined, label: string) {
  if (!value) return undefined;
  if (!/^[A-Z]{3}$/.test(value.currency)) throw new Error(`${label} needs a currency code.`);
  return validateMoney(value, value.currency, label);
}

function validateMoney(value: Money | undefined, currency: string, label: string) {
  if (!value) return undefined;
  if (!Number.isInteger(value.amountMinor) || value.amountMinor <= 0) {
    throw new Error(`${label} must be greater than zero.`);
  }
  if (value.currency !== currency) {
    throw new Error(`${label} must use ${currency}.`);
  }
  return value;
}

function optionalText(value: string | undefined) {
  const trimmed = value?.trim();
  return trimmed || undefined;
}

function optionalTextValue(value: unknown) {
  return typeof value === "string" ? optionalText(value) : undefined;
}

function recordValue(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function requiredText(value: string | undefined, label: string) {
  const text = optionalText(value);
  if (!text) throw new Error(`${label} is required.`);
  return text;
}

function requiredEnum<T extends string>(value: T, values: Set<string>, label: string): T {
  if (!values.has(value)) throw new Error(`Choose a valid ${label}.`);
  return value;
}

function optionalEnum<T extends string>(value: T | undefined, values: Set<string>, label: string) {
  return value === undefined ? undefined : requiredEnum(value, values, label);
}

function validReferences(values: string[] | undefined, allowed: Set<string>) {
  return Array.isArray(values) ? Array.from(new Set(values.filter((value) => allowed.has(value)))) : [];
}

function nonNegativeInteger(value: number, label: string) {
  if (!Number.isInteger(value) || value < 0) throw new Error(`${label} is invalid.`);
  return value;
}

function validateOptionalWebURL(value: string | undefined, label: string) {
  const normalized = optionalText(value);
  if (normalized && !isWebURL(normalized)) throw new Error(`${label} must use http or https.`);
  return normalized;
}

function validateOptionalEvidenceImageURL(value: string | undefined) {
  const normalized = optionalText(value);
  if (normalized && !isWebURL(normalized) && !isSafeRootedAssetURL(normalized)) {
    throw new Error("Evidence image links must use http or https, or a safe workspace asset path.");
  }
  return normalized;
}

function optionalTimestamp(value: string | undefined, label: string) {
  if (!value) return undefined;
  if (!validTimestamp(value)) throw new Error(`${label} is invalid.`);
  return value;
}

function validTimestamp(value: string) {
  return typeof value === "string" && Number.isFinite(new Date(value).getTime());
}

function isWebURL(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function isSafeRootedAssetURL(value: string) {
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\")) {
    return false;
  }
  try {
    const url = new URL(value, "https://workspace.listwork.invalid");
    return url.origin === "https://workspace.listwork.invalid" && url.pathname.startsWith("/");
  } catch {
    return false;
  }
}
